import { AppLocale, getLocaleLanguage, getStoredLocale } from 'modules/i18n';
import { FormDefinition, LocalizedText } from './api-types';

/**
 * The locale to read a definition in.
 *
 * The app's current language wins when the form was authored in it; otherwise the form's first
 * declared language does. Showing a question in a language the form does not have would render
 * blank, and a blank question is worse than one in the wrong language — the respondent can still
 * read it, or ask.
 */
export const resolveLocale = (definition: FormDefinition | null): string => {
  // AppLocale's values already ARE the locale tags the definition uses ('de-DE', 'en-US').
  const appLocale: string = getLocaleLanguage();
  const declared = definition?.languages ?? [];
  if (declared.includes(appLocale)) {
    return appLocale;
  }
  return declared[0] ?? appLocale;
};

/**
 * Localized text in the requested locale, falling back to any locale the text does have.
 *
 * The server enforces completeness per declared locale at publish, so the fallback should be dead
 * code — but it is the difference between an imperfect question and an empty screen, which is not a
 * trade worth taking for strictness.
 */
export const textFor = (
  text: LocalizedText | null | undefined,
  locale: string
): string => {
  if (!text) {
    return '';
  }
  if (typeof text[locale] === 'string') {
    return text[locale];
  }
  const first = Object.values(text).find(value => typeof value === 'string');
  return first ?? '';
};

/**
 * The app can only render its own chrome in a language it ships bundles for.
 *
 * `resolveLocale` will happily answer with the form's first declared language, and a definition is
 * authored elsewhere — a form written in French resolves to `fr-FR`, which this app has no
 * translations for. Switching to it would replace working German chrome with missing strings, and
 * burn a loading pause and an error log getting there.
 *
 * So the questions still render in whatever language the definition has (that comes from the
 * definition, not from here), and the words around them stay as they were. Wrong-language chrome is
 * bad; absent chrome is worse.
 */
const APP_LOCALES: string[] = Object.values(AppLocale);

const renderableByTheApp = (locale: string): string =>
  APP_LOCALES.includes(locale) ? locale : getLocaleLanguage();

/**
 * Whether this form gets the language feature at all: it must be published in BOTH languages the app
 * itself speaks.
 *
 * "More than one declared language" is a different test and the wrong one. The switcher always offers
 * German and English, so a form written in German and French would show a flag whose only destination
 * is a language the form does not have — every question falling back and the chrome in English. And a
 * form in a single language has nothing to choose between, so neither the flag nor the automatic pick
 * has anything to do.
 */
export const isLanguageSwitchable = (
  definition: FormDefinition | null
): boolean => {
  const declared = definition?.languages ?? [];
  /*
    Read against APP_LOCALES rather than a second hardcoded pair, so the set the app speaks is
    written down once.

    `every`, not "two or more", and the difference matters: the shared switcher renders a fixed
    German and English menu, so the gate has to mean "the form has everything the switcher can offer"
    — not "the form has two languages the app knows". A form in German and French would satisfy the
    looser test and then show a flag whose only destination is an English it does not have.

    That ties this to the switcher's own options. If AppLocale ever gains a third language, BOTH move
    together: the switcher grows a menu item and this stops being an `every`.
  */
  return APP_LOCALES.every(locale => declared.includes(locale));
};

/**
 * The locale the device asks for, if the form has it.
 *
 * Matched on the BASE language, not the exact tag: a phone set to `en-GB` or `de-AT` should get the
 * English or German the form was actually written in, and requiring `en-US` exactly would send most
 * English speakers to German. `navigator.languages` is preferred over `navigator.language` because it
 * is the ordered list of what somebody actually reads, and the first entry is only their top choice.
 */
const deviceLanguagesAsked = (): readonly string[] => {
  if (typeof navigator === 'undefined') {
    return [];
  }
  return navigator.languages?.length
    ? navigator.languages
    : [navigator.language];
};

const deviceLocaleAmong = (declared: string[]): string | null => {
  for (const tag of deviceLanguagesAsked()) {
    if (!tag) {
      continue;
    }
    const exact = declared.find(
      language => language.toLowerCase() === tag.toLowerCase()
    );
    if (exact) {
      return exact;
    }
    const base = tag.split('-')[0].toLowerCase();
    const sameLanguage = declared.find(
      language => language.split('-')[0].toLowerCase() === base
    );
    if (sameLanguage) {
      return sameLanguage;
    }
  }
  return null;
};

/**
 * Which language to open a form in, before anybody has touched the flag.
 *
 * Order matters, and each step is there to avoid a specific wrong answer:
 *
 * 1. **An explicit choice wins.** Somebody who has set their language in the app — or used the flag
 *    on a form — has said what they read. The device is a guess; that is not.
 * 2. **Otherwise the device's language**, if the form was written in it. This is the case the app
 *    has never handled: `getCurrentLocale` falls back to German for anyone who has never chosen, so
 *    an English phone opening a bilingual form got German.
 * 3. **Otherwise the language already loaded**, if the form has it. Nothing is gained by moving a
 *    reader off a language the form was written in.
 * 4. **Otherwise the form's first declared language** — the one the operator led with.
 *
 * A form NOT published in both languages skips all of that and simply follows {@link resolveLocale}:
 * there is no choice to make, but the chrome must still match the language the questions will be
 * rendered in.
 *
 * Distinct from {@link resolveLocale}, which answers "which locale can this definition be READ in"
 * given whatever the app is already set to. This one decides what the app should be set to.
 */
export const preferredLocale = (definition: FormDefinition | null): string => {
  /*
   * No CHOICE to offer unless the form is in both languages — the same gate the flag uses.
   *
   * But the chrome still has to follow the questions. A form published only in English renders its
   * questions in English however the app is set, and every `getTrans` string around them — the
   * item-scale hint, the "{done} von {total} bewertet" counter, the self-assessment note — would stay
   * German. That is the mixed screen this returns `resolveLocale` to prevent: whatever language the
   * questions end up in, the words around them match it.
   */
  if (!isLanguageSwitchable(definition)) {
    return renderableByTheApp(resolveLocale(definition));
  }

  const declared = (definition?.languages ?? []).filter(Boolean);

  const chosen = getStoredLocale();
  if (chosen && declared.includes(chosen)) {
    return chosen;
  }

  /*
   * Restricted to languages the app can actually render before matching, not after: `declared` can
   * carry a language beyond the pair the switcher offers (`isLanguageSwitchable` requires German and
   * English to both be present, not that they are the only two), and a device asking for that extra
   * language would otherwise win the match and only get corrected by the fallback below — skipping a
   * supported language listed after it on the same device.
   */
  const device = deviceLocaleAmong(
    declared.filter(language => APP_LOCALES.includes(language))
  );
  if (device) {
    return device;
  }

  const app = getLocaleLanguage();
  return renderableByTheApp(declared.includes(app) ? app : declared[0]);
};
